/** Resolve init options into a concrete config (design §1.2, §11.1). */
import type { ConsentMode, InitOptions, ResolvedConfig } from './types';
import { normalizePathAllowlist } from './path-policy';

/**
 * Fail-closed (attr-security): if consentMode is absent/malformed, treat as the
 * SAFEST mode — `strict` — so a misconfigured snippet does not default to
 * persistent tracking. attr-ui bakes the real mode into the snippet; this is the
 * misconfiguration guard. (We do NOT implement send-suppression hard-gate in MVP
 * per team-lead; storage persistence still fails closed under strict + unknown consent.)
 */
function safeConsentMode(m: unknown): ConsentMode {
  return m === 'strict' || m === 'balanced' || m === 'permissive' ? m : 'strict';
}

/** Derive the collector origin from the script's own src (first-party CNAME friendly). */
function scriptOrigin(): string {
  try {
    const cur = (document.currentScript as HTMLScriptElement | null) || null;
    let src = cur && cur.src ? cur.src : '';
    if (!src) {
      const scripts = document.getElementsByTagName('script');
      for (let i = scripts.length - 1; i >= 0; i--) {
        const s = scripts[i].src || '';
        if (/\/pixel\/v\d+(\.\w+)?\.js(\?|$)/.test(s)) {
          src = s;
          break;
        }
      }
    }
    if (src) return new URL(src, location.href).origin;
  } catch {
    /* noop */
  }
  try {
    return location.origin;
  } catch {
    return '';
  }
}

export function resolveConfig(
  publicKey: string,
  opts: InitOptions,
): ResolvedConfig {
  const origin =
    (opts.collectorUrl ? stripPath(opts.collectorUrl) : scriptOrigin()) || "";
  const base = origin.replace(/\/$/, "");
  return {
    publicKey,
    behavior: opts.behavior === true,
    storageNamespace:
      opts.storageNamespace &&
      /^[A-Za-z0-9_-]{1,64}$/.test(opts.storageNamespace)
        ? opts.storageNamespace
        : undefined,
    clientId: opts.clientId,
    collectorUrl:
      opts.collectorUrl && /\/api\//.test(opts.collectorUrl)
        ? opts.collectorUrl
        : base + "/api/track/v1/collect",
    collectGifUrl: base + "/p.gif",
    cookieDomain: opts.cookieDomain,
    consentMode: safeConsentMode(opts.consentMode),
    clientHardGate: opts.clientHardGate === true,
    collectionPaths:
      opts.collectionPaths === undefined
        ? undefined
        : normalizePathAllowlist(opts.collectionPaths),
    identityAllowed: opts.identityAllowed !== false,
    metadataOnly: opts.metadataOnly === true,
    sensitiveMode: opts.sensitiveMode === true,
    pathAllowlist: normalizePathAllowlist(opts.pathAllowlist),
    storageMode: opts.storageMode || "auto",
    autoPageView: opts.autoPageView !== false,
    spaTracking: opts.spaTracking !== false,
    sessionTimeoutMs: Math.max(1, opts.sessionTimeoutMinutes || 30) * 60 * 1000,
    crossDomain: Array.isArray(opts.crossDomain) ? opts.crossDomain : [],
    paramAllowlist: opts.paramAllowlist,
  };
}

function stripPath(u: string): string {
  try {
    return new URL(u, location.href).origin;
  } catch {
    return u;
  }
}
